import "../../../../.cache/static-styles-react/build-javascript/compiled.js";
/** @jsxRuntime automatic @jsxImportSource @terraindigital/static-styles-react */
import { mergeProps as _mergeProps, mergeComponentProps as _mergeComponentProps, mergeExternalProps as _mergeExternalProps, cssRef as _cssRef } from "@terraindigital/static-styles-react";
const _staticStyles = _cssRef("menu-item__link-3dvsht", {
  gap: 0
});
const _staticStyles2 = _cssRef("menu-item__div-3dvsht", {
  gap: 0
});
import React from 'react';
import Caret from './caret';
import NavItem from './nav-item';
import * as NavigationMenu from '../../primitives/navigation-menu';
import type { SiteNavigationPrimary, SiteNavigationPrimaryLevel, SiteNavigationPrimarySublevel } from '../navigation/site-navigation-types';
import { MenuImage } from '../types';
import Link from '../../primitives/link';
import Typography from '../../primitives/typography';
import Image from '../../primitives/image';
import styles, { MEGAMENU_CATEGORY_ICON_SIZES, MEGAMENU_SERVICE_ICON_SIZES, MEGAMENU_IMAGE_WIDTH } from './menu-item.styles';
import hasNavigableHref from '../has-navigable-href';

// Rich text / plain-text fields from Contentstack may include character references (e.g. &nbsp;)
// without angle brackets; React text nodes show those literally unless we parse them as HTML.
const HTML_TAG_RE = /<[^>]*>/;
const HTML_CHAR_REF_RE = /&(?:#(?:x[\da-fA-F]+|\d+)|[a-zA-Z][\da-zA-Z]*);/;
const hasHtmlTags = (content: string): boolean => HTML_TAG_RE.test(content);
const hasHtmlCharacterReferences = (content: string): boolean => HTML_CHAR_REF_RE.test(content);
const needsInnerHtmlParsing = (content: string): boolean => hasHtmlTags(content) || hasHtmlCharacterReferences(content);

// Category panel title/description fallbacks when CMS omits copy
const getCategoryContent = (categoryData: SiteNavigationPrimaryLevel): [string, string] => {
  const baseTitle = categoryData?.title || categoryData?.link || 'Service';
  const title = categoryData?.itemInfo?.title || categoryData?.title || `${categoryData?.link ? baseTitle : 'Service'} Cleanup and Restoration Services`;
  const description = categoryData?.itemInfo?.contentAsPlainText || categoryData?.itemInfo?.content || `We provide comprehensive ${(categoryData?.title || 'service').toLowerCase()} services to restore your property quickly and efficiently.`;
  return [title, description];
};
type ItemInfoImageEl = NonNullable<NonNullable<SiteNavigationPrimary['itemInfo']>['image']>[number];
export const firstItemInfoImageToMenuImage = (itemInfo: SiteNavigationPrimary['itemInfo'] | undefined | null): MenuImage | undefined => {
  const file = itemInfo?.image?.find((f: ItemInfoImageEl | null | undefined) => Boolean(f?.url));
  if (!file?.url) return undefined;
  const base: MenuImage = {
    title: file.title ?? file.filename ?? '',
    url: file.url
  };
  if (file.contentStackAsset != null && typeof file.contentStackAsset === 'object') {
    return {
      ...base,
      contentStackAsset: file.contentStackAsset
    };
  }
  return base;
};
const sublevelIconToMenuImage = (sublevel: SiteNavigationPrimarySublevel): MenuImage | null => {
  if (!sublevel?.icon?.url) return null;
  return {
    title: sublevel.icon.title ?? '',
    url: sublevel.icon.url
  };
};
export type MenuItemProps = {
  tab: SiteNavigationPrimary;
  isTouchDevice: boolean;
};
function useResolvedMenuFields(tab: SiteNavigationPrimary): {
  href: string | undefined;
  title: string;
  childMenu: SiteNavigationPrimaryLevel[];
  mainNavItemTitle: string | undefined;
  mainNavItemDescription: string | undefined;
  mainNavItemImage: MenuImage | undefined;
} {
  const title = tab.title ?? '';
  const centerPlain = tab.itemInfo?.contentAsPlainText || tab.itemInfo?.content || '';
  const centerTitle = tab.itemInfo?.title ?? undefined;
  return {
    href: tab.link ?? undefined,
    title,
    childMenu: (tab.items ?? []).filter((row: SiteNavigationPrimaryLevel | null | undefined): row is SiteNavigationPrimaryLevel => Boolean(row)),
    mainNavItemTitle: centerTitle || title,
    mainNavItemDescription: centerPlain || undefined,
    mainNavItemImage: firstItemInfoImageToMenuImage(tab.itemInfo)
  };
}
const menuHeadingDomId = (menuTitle: string): string => `menu-title-${menuTitle.replace(/\s+/g, '-').toLowerCase()}`;
const CategoryPanelTitle = ({
  tabTitle,
  displayTitle
}: {
  tabTitle: string;
  displayTitle: string;
}): JSX.Element => {
  const id = menuHeadingDomId(tabTitle);
  if (!needsInnerHtmlParsing(displayTitle)) {
    return <h2 {..._mergeProps({
      id: id,
      css: styles.contentTitle
    })}>{displayTitle}</h2>;
  }
  if (hasHtmlTags(displayTitle)) {
    return <div {..._mergeProps({
      id: id,
      css: styles.contentTitle,
      dangerouslySetInnerHTML: {
        __html: displayTitle
      }
    })} />;
  }
  return <h2 {..._mergeProps({
    id: id,
    css: styles.contentTitle,
    dangerouslySetInnerHTML: {
      __html: displayTitle
    }
  })} />;
};
const CategoryPanelDescription = ({
  description
}: {
  description: string;
}): JSX.Element => {
  if (!needsInnerHtmlParsing(description)) {
    return <p {..._mergeProps({
      css: styles.contentDescription
    })}>{description}</p>;
  }
  if (hasHtmlTags(description)) {
    return <div {..._mergeProps({
      css: styles.contentDescription,
      dangerouslySetInnerHTML: {
        __html: description
      }
    })} />;
  }
  return <p {..._mergeProps({
    css: styles.contentDescription,
    dangerouslySetInnerHTML: {
      __html: description
    }
  })} />;
};
const MenuItem = React.memo((props: MenuItemProps): JSX.Element => {
  const {
    isTouchDevice,
    tab
  } = props;
  const {
    href,
    title,
    childMenu,
    mainNavItemTitle,
    mainNavItemDescription,
    mainNavItemImage
  } = useResolvedMenuFields(tab);
  const childMenuMemo = React.useMemo(() => childMenu || [], [childMenu]);
  const hasChildren = childMenuMemo.length > 0;
  // Desktop only: separate parent link from disclosure button (no nested interactive).
  const showSplitTrigger = hasNavigableHref(href) && !isTouchDevice;
  const [activeCategory, setActiveCategory] = React.useState<string | null>(null);
  const menuRegionRef = React.useRef<HTMLDivElement>(null);
  const handleCategoryChange = React.useCallback((nodeTitle: string) => {
    setActiveCategory(nodeTitle);
  }, []);
  React.useEffect(() => {
    const content = menuRegionRef.current?.closest<HTMLElement>('[data-menu="content"]');
    if (!hasChildren || !content) return undefined;
    const observer = new MutationObserver(() => {
      if (content.dataset.state === 'visible') {
        setActiveCategory(null);
      }
    });
    observer.observe(content, {
      attributes: true,
      attributeFilter: ['data-state']
    });
    return () => observer.disconnect();
  }, [hasChildren]);
  const activeCategoryData = React.useMemo(() => activeCategory ? childMenuMemo.find(cat => cat?.title === activeCategory) : null, [activeCategory, childMenuMemo]);
  const [categoryDisplayTitle, categoryDescription] = React.useMemo(() => activeCategoryData ? getCategoryContent(activeCategoryData) : [mainNavItemTitle || title, mainNavItemDescription || `Explore our comprehensive ${title.toLowerCase()} services.`], [activeCategoryData, mainNavItemTitle, mainNavItemDescription, title]);
  if (!hasChildren) {
    return <NavItem {..._mergeComponentProps({
      to: href,
      css: styles.navItem
    })}>
                {title}
            </NavItem>;
  }
  return <>
            {showSplitTrigger ? <div {..._mergeProps({
      "data-menu": "trigger-host",
      css: styles.triggerHost
    })}>
                    <NavItem {..._mergeComponentProps({
        to: href,
        css: styles.navItem
      })}>
                        {title}
                    </NavItem>
                    <NavigationMenu.Trigger {..._mergeExternalProps({
        css: styles.triggerDisclosure,
        "aria-label": `Open ${title} menu`
      })}>
                        <Caret {..._mergeComponentProps({
          css: styles.caret,
          "aria-hidden": "true"
        })} />
                    </NavigationMenu.Trigger>
                </div> : <NavigationMenu.Trigger {..._mergeExternalProps({
      css: [styles.trigger, isTouchDevice && hasChildren && styles.triggerTouchMode]
    })}>
                    <NavItem>
                        {title}
                        <Caret {..._mergeComponentProps({
          css: styles.caret,
          "aria-hidden": "true"
        })} />
                    </NavItem>
                </NavigationMenu.Trigger>}
            <NavigationMenu.Content {..._mergeExternalProps({
      animationTimeMs: 400,
      css: styles.content,
      mode: "center"
    })}>
                <div {..._mergeProps({
        ref: menuRegionRef,
        role: "region",
        "aria-label": `${title} menu`,
        "aria-labelledby": `menu-title-${title.replace(/\s+/g, '-').toLowerCase()}`,
        css: styles.outerContainer
      })}>
                    <div {..._mergeProps({
          css: styles.heroBackground,
          "aria-hidden": "true"
        })} />

                    <nav {..._mergeProps({
          css: styles.categoriesSection,
          "aria-label": "Service categories"
        })}>
                        <div {..._mergeProps({
            css: styles.categoriesList
          })}>
                            {childMenuMemo.map(node => {
              if (!node || !node.title) return null;
              const nodeTitle = node.title || '';
              const nodeHref = node.link ?? undefined;
              const leaves = (node.items ?? []).filter((l: SiteNavigationPrimarySublevel | null | undefined): l is SiteNavigationPrimarySublevel => Boolean(l?.title && l?.link));
              const hasSubItems = leaves.length > 0;
              if (!nodeTitle) return null;
              const effectiveNodeHref = isTouchDevice && hasSubItems ? undefined : nodeHref;
              const rowIcon = node.icon?.url ? {
                title: node.icon.title ?? '',
                url: node.icon.url
              } : null;
              const categoryContent = <>
                                        {rowIcon && <div {..._mergeProps({
                  css: styles.categoryIcon,
                  "aria-hidden": "true"
                })}>
                                                <Image image={rowIcon} sizes={MEGAMENU_CATEGORY_ICON_SIZES} />
                                            </div>}
                                        <span {..._mergeProps({
                  className: "category-text",
                  css: [styles.categoryText, activeCategory === nodeTitle && styles.activeCategoryText]
                })}>
                                            {nodeTitle}
                                        </span>
                                        {activeCategory === nodeTitle && <div {..._mergeProps({
                  className: "category-arrow",
                  css: styles.categoryArrow,
                  "aria-hidden": "true"
                })}>
                                                <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
                                                    <path d="M1 1L6 6L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>}
                                    </>;
              const handleInteraction = (): void => handleCategoryChange(nodeTitle);
              const handleKeyDown = (e: React.KeyboardEvent): void => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCategoryChange(nodeTitle);
                }
              };
              const hasIcon = !!rowIcon;
              return effectiveNodeHref ? <Link {..._mergeComponentProps({
                key: nodeTitle,
                to: effectiveNodeHref,
                css: [styles.categoryItem, activeCategory === nodeTitle && styles.activeCategoryItem, !hasIcon && _staticStyles],
                onMouseEnter: handleInteraction,
                onTouchStart: handleInteraction,
                onFocus: handleInteraction,
                "aria-current": activeCategory === nodeTitle ? 'true' : undefined
              })}>
                                        {categoryContent}
                                    </Link> : <div {..._mergeProps({
                key: nodeTitle,
                role: "button",
                tabIndex: 0,
                css: [styles.categoryItem, activeCategory === nodeTitle && styles.activeCategoryItem, styles.categoryItemTouchDisabled, !hasIcon && _staticStyles2],
                onMouseEnter: handleInteraction,
                onTouchStart: handleInteraction,
                onFocus: handleInteraction,
                onKeyDown: handleKeyDown,
                "aria-pressed": activeCategory === nodeTitle
              })}>
                                        {categoryContent}
                                    </div>;
            })}
                        </div>
                    </nav>

                    <div {..._mergeProps({
          css: styles.contentSection
        })}>
                        <CategoryPanelTitle tabTitle={title} displayTitle={categoryDisplayTitle} />
                        <CategoryPanelDescription description={categoryDescription} />
                        {(() => {
            const hasSubItems = activeCategoryData?.items && activeCategoryData.items.length > 0;
            if (!activeCategory || !hasSubItems || !activeCategoryData?.items) return null;
            return <nav {..._mergeProps({
              css: styles.servicesList,
              "aria-label": `${activeCategory} services`
            })}>
                                    {activeCategoryData.items.map((leaf: SiteNavigationPrimarySublevel | null | undefined) => {
                if (!leaf || !leaf.link || !leaf.title) return null;
                const subHref = leaf.link;
                const subTitle = leaf.title;
                const leafIcon = sublevelIconToMenuImage(leaf);
                const categoryIcon = activeCategoryData.icon?.url ? {
                  title: activeCategoryData.icon.title ?? '',
                  url: activeCategoryData.icon.url
                } : null;
                return <Typography {..._mergeComponentProps({
                  key: `link-${subHref}-${subTitle}`,
                  as: Link,
                  to: subHref,
                  css: styles.serviceItem,
                  fontSize: "lg"
                })}>
                                                <div {..._mergeProps({
                    css: styles.serviceIcon,
                    "aria-hidden": "true"
                  })}>
                                                    {(leafIcon || categoryIcon) && <Image image={leafIcon || categoryIcon!} sizes={MEGAMENU_SERVICE_ICON_SIZES} />}
                                                </div>
                                                <span {..._mergeProps({
                    css: styles.serviceText
                  })}>{subTitle}</span>
                                            </Typography>;
              })}
                                </nav>;
          })()}
                    </div>

                    {(firstItemInfoImageToMenuImage(activeCategoryData?.itemInfo ?? null) || mainNavItemImage) && <div {..._mergeProps({
          css: styles.imageSection
        })}>
                            <div {..._mergeProps({
            css: styles.megamenuContentImageFrame
          })}>
                                <Image {..._mergeComponentProps({
              image: firstItemInfoImageToMenuImage(activeCategoryData?.itemInfo ?? null) || mainNavItemImage!,
              css: styles.contentImage,
              sizes: MEGAMENU_IMAGE_WIDTH,
              objectFit: "cover",
              imgStyle: {
                objectFit: 'cover',
                objectPosition: 'center center'
              }
            })} />
                            </div>
                        </div>}
                </div>
            </NavigationMenu.Content>
        </>;
});
MenuItem.displayName = 'MenuItem';
export default MenuItem;